import { Controller } from "@hotwired/stimulus"
import { getDocument } from "pdfjs-dist/build/pdf.mjs"
import { WorkerMessageHandler } from "pdfjs-dist/build/pdf.worker.mjs"

const ZOOM_STORAGE_KEY = "invoice_pdf_zoom_percent"

if (!globalThis.pdfjsWorker?.WorkerMessageHandler) {
  globalThis.pdfjsWorker = {
    ...(globalThis.pdfjsWorker || {}),
    WorkerMessageHandler,
  }
}

export default class extends Controller {
  static targets = ["viewport", "zoomLabel", "status"]
  static values = {
    defaultZoom: Number,
    url: String,
  }

  async connect() {
    if (!this.hasUrlValue || !this.hasViewportTarget) return

    this.zoom = this.initialZoom()
    this.renderGeneration = 0
    this.pdfDocument = null

    this.updateZoomLabel()
    await this.loadAndRenderDocument()
  }

  disconnect() {
    this.renderGeneration += 1
    if (this.pdfDocument?.destroy) this.pdfDocument.destroy()
    this.pdfDocument = null
  }

  async zoomIn(event) {
    event.preventDefault()
    await this.setZoom(this.zoom + 10)
  }

  async zoomOut(event) {
    event.preventDefault()
    await this.setZoom(this.zoom - 10)
  }

  async resetZoom(event) {
    event.preventDefault()
    await this.setZoom(100)
  }

  initialZoom() {
    const persistedZoom = this.readPersistedZoom()
    if (persistedZoom !== null) return persistedZoom

    if (this.hasDefaultZoomValue && Number.isFinite(this.defaultZoomValue)) {
      return this.clampZoom(this.defaultZoomValue)
    }

    return 110
  }

  clampZoom(value) {
    return Math.max(50, Math.min(300, value))
  }

  async setZoom(value) {
    const normalized = this.clampZoom(Math.round(value / 5) * 5)
    if (normalized === this.zoom) return

    this.zoom = normalized
    this.persistZoom(normalized)
    this.updateZoomLabel()

    if (!this.pdfDocument) return

    await this.renderDocumentPages()
  }

  updateZoomLabel() {
    if (!this.hasZoomLabelTarget) return

    this.zoomLabelTarget.textContent = `${this.zoom}%`
  }

  async loadAndRenderDocument() {
    try {
      this.setStatus("Loading PDF preview...")
      const loadingTask = getDocument({
        url: this.urlValue,
        withCredentials: true,
      })
      this.pdfDocument = await loadingTask.promise
      await this.renderDocumentPages()
      this.setStatus("")
    } catch (error) {
      console.error("PDF preview failed to load", error)
      this.setStatus("Could not load PDF.js preview.")
    }
  }

  async renderDocumentPages() {
    if (!this.pdfDocument || !this.hasViewportTarget) return

    const generation = ++this.renderGeneration
    this.setStatus(`Rendering at ${this.zoom}%...`)
    this.viewportTarget.replaceChildren()

    for (let pageNumber = 1; pageNumber <= this.pdfDocument.numPages; pageNumber += 1) {
      if (generation !== this.renderGeneration) return

      const page = await this.pdfDocument.getPage(pageNumber)
      if (generation !== this.renderGeneration) return

      const pageElement = await this.renderPage(page)
      if (generation !== this.renderGeneration) return

      this.viewportTarget.appendChild(pageElement)
    }

    if (generation === this.renderGeneration) this.setStatus("")
  }

  async renderPage(page) {
    const viewport = page.getViewport({ scale: this.zoom / 100 })
    const outputScale = window.devicePixelRatio || 1

    const wrapper = document.createElement("div")
    wrapper.className = "invoice-pdf__page"

    const canvas = document.createElement("canvas")
    canvas.className = "invoice-pdf__canvas"
    canvas.width = Math.floor(viewport.width * outputScale)
    canvas.height = Math.floor(viewport.height * outputScale)
    canvas.style.width = `${Math.floor(viewport.width)}px`
    canvas.style.height = `${Math.floor(viewport.height)}px`

    const context = canvas.getContext("2d", { alpha: false })
    const transform = outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0]

    await page.render({
      canvasContext: context,
      viewport,
      transform,
    }).promise

    wrapper.appendChild(canvas)
    return wrapper
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("invoice-is-hidden", message === "")
  }

  readPersistedZoom() {
    try {
      const raw = window.localStorage.getItem(ZOOM_STORAGE_KEY)
      if (!raw) return null

      const parsed = Number.parseInt(raw, 10)
      if (!Number.isFinite(parsed)) return null

      return this.clampZoom(parsed)
    } catch {
      return null
    }
  }

  persistZoom(value) {
    try {
      window.localStorage.setItem(ZOOM_STORAGE_KEY, String(value))
    } catch {
      // Ignore storage errors (private browsing, storage disabled, etc.)
    }
  }
}
